import CallMade from '@mui/icons-material/CallMade';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import { green, lightGreen } from '@mui/material/colors';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import Configuracao from '../../util/Configuracao';
import ReactComponent from '../ReactComponent';
import ModalConfirmacao from './ModalConfirmacao';
import { Box, Stack } from '@mui/system';

class ModalEnviarProcessoPJe extends ReactComponent {
    dialogEnviarPJe = false;
    movimentoCNJ = false;
    acordao = false;
    certidao = false;
    loading = false;
    processando = false;
    openModalConfirmacaoAcordaosNaoVerificados = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            dialogEnviarPJe: observable,
            movimentoCNJ: observable,
            acordao: observable,
            certidao: observable,
            loading: observable,
            processando: observable,
            openModalConfirmacaoAcordaosNaoVerificados: observable,
            registro: computed,
            processo: computed,
            acordaoAssinado: computed,
            acordaoTransferido: computed,
            origemPJe: computed,
            handleEnviarPJe: action,
            changeLoading: action,
            handleChangeCheckbox: action,
            cancelar: action,
            transferirCertidao: action,
            transferirAcordao: action,
            transferirMovimentos: action,
            handleEnviar: action,
            fecharModalConfirmacaoAcordaosNaoVerificados: action,
            enviar: action,
            exibirIcone: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get processo() {
        return this.registro.processo;
    }

    get acordaoAssinado() {
        return this.registro.acordaoAssinado || this.registro.dataAssinaturaSistemaOrigem != null;
    }

    get acordaoTransferido() {
        return this.registro.acordaoTransferido || this.registro.dataAssinaturaSistemaOrigem != null;
    }

    get origemPJe() {
        return this.registro && this.registro.processo.origem === Configuracao.ORIGEM_PJE;
    }

    handleEnviarPJe = async () => {
        this.loading = true;
        this.dialogEnviarPJe = true;
        this.changeLoading();
    };

    changeLoading() {
        this.loading = !this.loading;
    }

    handleChangeCheckbox = (opcao) => {
        if (opcao === 'movimentoCNJ') {
            this.registro.lancarMovimentos = !this.registro.lancarMovimentos;
        } else if (opcao === 'certidao') {
            this.registro.lancarCertidao = !this.registro.lancarCertidao;
        } else {
            this.registro.lancarAcordao = !this.registro.lancarAcordao;
        }
    };

    cancelar = () => {
        this.registro.lancarMovimentos = false;
        this.registro.lancarAcordao = false;
        this.registro.lancarCertidao = false;
        this.dialogEnviarPJe = false;
    };

    transferirCertidao = () => {
        this.registro.certidaoJulgamentoTransferida = true;
    };

    transferirAcordao = () => {
        this.registro.acordaoTransferido = true;
    };

    transferirMovimentos = () => {
        this.registro.movimentosTransferidos = true;
    };

    handleEnviar = () => {
        if (this.registro.lancarAcordao && this.registro.acordaoValidado === false) {
            this.openModalConfirmacaoAcordaosNaoVerificados = true;
        } else {
            this.enviar();
        }
    };

    fecharModalConfirmacaoAcordaosNaoVerificados = () => {
        this.openModalConfirmacaoAcordaosNaoVerificados = false;
    };

    enviar = async () => {
        this.processando = true;
        this.openModalConfirmacaoAcordaosNaoVerificados = false;
        let processos = [];
        let sucesso = 0;
        processos.push(this.registro.processo.numeroCompleto);

        try {
            if (this.registro.lancarMovimentos) {
                const notificarSessao =
                    this.registro.informacoesAdicionais &&
                    this.registro.processo.relator !== this.registro.informacoesAdicionais.redator;
                let retorno = await this.movimentoCNJService.transferirMovimentos(
                    this.registro.sessaoId,
                    processos,
                    notificarSessao
                );
                console.log(retorno);
                if (retorno.status === 200 && retorno.data && retorno.data.length > 0) {
                    const resultado = retorno.data[0];
                    if (resultado.tipo === 'ERRO') {
                        this.mensagemStore.abrirMensagemErro(resultado.mensagem);
                    } else {
                        sucesso++;
                        this.transferirMovimentos();
                    }
                }
            }

            if (this.registro.lancarCertidao && this.registro.movimentosTransferidos) {
                let retorno = await this.certidaoService.transferirCertidao(this.registro.sessaoId, processos);
                if (retorno.status === 200 && retorno.data && retorno.data.length > 0) {
                    const resultado = retorno.data[0];
                    if (resultado.tipo === 'ERRO') {
                        this.mensagemStore.abrirMensagemErro(resultado.mensagem);
                    } else {
                        sucesso++;
                        this.transferirCertidao();
                    }
                }
            }

            if (
                this.registro.lancarAcordao &&
                (this.registro.movimentosTransferidos || this.registro.processo.origem === Configuracao.ORIGEM_SIJ)
            ) {
                let retorno = await this.acordaoService.transferirAcordao(this.registro.sessaoId, processos);
                if (retorno.status === 200 && retorno.data && retorno.data.length > 0) {
                    const resultado = retorno.data[0];
                    if (resultado.tipo === 'ERRO') {
                        this.mensagemStore.abrirMensagemErro(resultado.mensagem);
                    } else {
                        sucesso++;
                        this.transferirAcordao();
                    }
                }
            }

            if (sucesso > 0) {
                this.cancelar();
                await this.salaSessaoStore.recarregarProcesso(this.registro, false, true);
                this.mensagemStore.abrirMensagemSucesso('Documento(s) enviado(s) com sucesso.');
            }
        } finally {
            runInAction(() => {
                this.processando = false;
            });
        }
    };

    isAcordaoDesabilitado() {
        return (
            this.registro.acordaoTransferido ||
            !this.registro.acordaoAssinado ||
            !this.registro.acordaoObrigatorio ||
            (this.registro.processo.origem === Configuracao.ORIGEM_PJE &&
                !this.registro.movimentosTransferidos &&
                !this.registro.lancarMovimentos)
        );
    }

    isCertidaoDesabilitada() {
        return (
            this.registro.certidaoJulgamentoTransferida ||
            !this.registro.certidaoJulgamentoAssinada ||
            (!this.registro.movimentosTransferidos && !this.registro.lancarMovimentos)
        );
    }

    isMovimentoDesabilitado() {
        return this.registro.movimentosTransferidos || !this.registro.movimentosCadastrados;
    }

    get exibirIcone() {
        return (
            this.salaSessaoStore.isProcessoApreciado(this.registro) &&
            (this.registro.processo.origem !== Configuracao.ORIGEM_SIJ ||
                this.isComponenteHabilitado('EnvioAcordaoSIJ'))
        );
    }

    todosEnviados = () => {
        return (
            this.registro.certidaoJulgamentoTransferida &&
            this.registro.movimentosTransferidos &&
            ((this.registro.acordaoObrigatorio && this.registro.acordaoTransferido) ||
                !this.registro.acordaoObrigatorio)
        );
    };

    render() {
        return (
            <>
                {this.exibirIcone && (
                    <IconButton
                        onClick={this.handleEnviarPJe}
                        style={{ padding: '8px' }}
                        size="large"
                    >
                        <Tooltip
                            title={
                                this.registro.processo.origem === Configuracao.ORIGEM_PJE
                                    ? 'Enviar para o PJe'
                                    : 'Enviar para o eSIJ'
                            }
                        >
                            <CallMade style={{ fill: this.todosEnviados() ? lightGreen[900] : '#000000' }} />
                        </Tooltip>
                    </IconButton>
                )}
                {this.dialogEnviarPJe && (
                    <Dialog
                        open={this.dialogEnviarPJe}
                        onClose={this.cancelar}
                        style={{ marginRight: '60px' }}
                    >
                        <DialogTitle>
                            Enviar dados do processo para o{' '}
                            {this.registro.processo.origem === Configuracao.ORIGEM_PJE ? 'PJe' : 'eSIJ'}
                        </DialogTitle>
                        <DialogContent>
                            {!this.loading ? (
                                <Box>
                                    {this.registro.processo.origem === Configuracao.ORIGEM_PJE && (
                                        <Box style={{ alignItems: 'end' }}>
                                            <Tooltip
                                                placement="top"
                                                title={this.renderTooltipMovimento()}
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems={'center'}
                                                >
                                                    <Checkbox
                                                        color="primary"
                                                        onChange={this.handleChangeCheckbox.bind(this, 'movimentoCNJ')}
                                                        checked={
                                                            this.registro.movimentosTransferidos ||
                                                            this.registro.lancarMovimentos
                                                        }
                                                        disabled={this.isMovimentoDesabilitado()}
                                                    />
                                                    {this.renderDescricaoMovimento()}
                                                </Stack>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    {this.parametroStore.usaCertidao() &&
                                        this.registro.processo.origem === Configuracao.ORIGEM_PJE && (
                                            <Box style={{ alignItems: 'end' }}>
                                                <Tooltip
                                                    placement="top"
                                                    title={this.renderTooltipCertidao()}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems={'center'}
                                                    >
                                                        <Checkbox
                                                            color="primary"
                                                            onChange={this.handleChangeCheckbox.bind(this, 'certidao')}
                                                            checked={
                                                                this.registro.certidaoJulgamentoTransferida ||
                                                                this.registro.lancarCertidao
                                                            }
                                                            disabled={this.isCertidaoDesabilitada()}
                                                        />
                                                        {this.renderDescricaoCertidao()}
                                                    </Stack>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    <Box>
                                        <Tooltip
                                            placement="top"
                                            title={this.renderTooltipAcordao()}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems={'center'}
                                            >
                                                <Checkbox
                                                    color="primary"
                                                    onChange={this.handleChangeCheckbox.bind(this, 'acordao')}
                                                    checked={
                                                        this.registro.acordaoTransferido || this.registro.lancarAcordao
                                                    }
                                                    disabled={this.isAcordaoDesabilitado()}
                                                />
                                                {this.renderDescricaoAcordao()}
                                            </Stack>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <CircularProgress size={25} /> Aguarde...
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={this.cancelar}
                                color="secondary"
                                variant="outlined"
                                endIcon={<CancelIcon />}
                                disabled={!!(this.loading || this.processando)}
                            >
                                Cancelar
                            </Button>
                            <Tooltip
                                title={
                                    !this.registro.lancarMovimentos &&
                                    !this.registro.lancarCertidao &&
                                    !this.registro.lancarAcordao
                                        ? 'Selecione pelo menos uma opção'
                                        : 'Enviar selecionado(s)'
                                }
                                placement="top"
                            >
                                <span>
                                    <Button
                                        onClick={this.handleEnviar}
                                        color="primary"
                                        variant="outlined"
                                        endIcon={<CallMade />}
                                        disabled={
                                            !!(
                                                this.processando ||
                                                (!this.registro.lancarMovimentos &&
                                                    !this.registro.lancarCertidao &&
                                                    !this.registro.lancarAcordao)
                                            )
                                        }
                                    >
                                        Enviar
                                    </Button>
                                </span>
                            </Tooltip>
                        </DialogActions>
                    </Dialog>
                )}

                <ModalConfirmacao
                    titulo={'Envio de informações para o PJe'}
                    open={this.openModalConfirmacaoAcordaosNaoVerificados}
                    callbackCancelar={this.fecharModalConfirmacaoAcordaosNaoVerificados}
                    callbackConfirmar={() => this.enviar()}
                >
                    <Typography display={'inline'}>
                        Atenção! Existem acórdãos não verificados pela secretaria. Deseja prosseguir?
                    </Typography>
                </ModalConfirmacao>
            </>
        );
    }

    renderDescricaoAcordao() {
        if (this.registro.acordaoTransferido) {
            return <span style={{ color: green[700] }}>Acórdão</span>;
        }
        if (this.isAcordaoDesabilitado()) {
            return <s style={{ color: 'grey' }}>Acórdão</s>;
        }
        return 'Acórdão';
    }

    renderDescricaoCertidao() {
        return this.registro.processo.origem === Configuracao.ORIGEM_PJE &&
            (!this.registro.certidaoJulgamentoAssinada ||
                (!this.registro.movimentosTransferidos && !this.registro.lancarMovimentos)) ? (
            <s style={{ color: 'grey' }}>Certidão</s>
        ) : this.registro.certidaoJulgamentoTransferida ? (
            <span style={{ color: green[700] }}>Certidão</span>
        ) : (
            'Certidão'
        );
    }

    renderDescricaoMovimento() {
        return !this.registro.movimentosCadastrados ? (
            <s style={{ color: 'grey' }}>Movimento CNJ</s>
        ) : this.registro.movimentosTransferidos ? (
            <span style={{ color: green[700] }}>Movimento CNJ</span>
        ) : (
            <Typography display={'inline'}>Movimento CNJ</Typography>
        );
    }

    renderTooltipAcordao() {
        let stringPje = 'PJe';
        let stringSIJ = 'eSIJ';
        if (this.registro.acordaoTransferido) {
            return (
                'Acórdão transferido em: ' +
                moment(this.registro.informacoesAdicionais.dataTransferenciaAcordao, 'DD-MM-YYYY-HH-mm-ss').format(
                    'DD/MM/YYYY [às] HH:mm:ss'
                )
            );
        } else if (!this.registro.acordaoAssinado && this.registro.dataAssinaturaSistemaOrigem) {
            return 'Acórdão assinado no sistema de origem';
        } else if (!this.registro.acordaoObrigatorio) {
            return 'Não haverá publicação de acórdão';
        } else if (!this.registro.acordaoAssinado) {
            return 'Acórdão não assinado';
        } else if (
            !this.registro.movimentosTransferidos &&
            !this.registro.lancarMovimentos &&
            this.registro.processo.origem === Configuracao.ORIGEM_PJE
        ) {
            return 'É obrigatório enviar o movimento antes ou junto ao acórdão';
        } else {
            return this.registro.processo.origem === Configuracao.ORIGEM_PJE
                ? 'Enviar acórdão para o ' + stringPje
                : 'Enviar acórdão para o ' + stringSIJ;
        }
    }

    renderTooltipCertidao() {
        return !this.registro.certidaoJulgamentoAssinada
            ? 'Certidão não assinada'
            : !this.registro.movimentosTransferidos && !this.registro.lancarMovimentos
              ? 'É obrigatório enviar o movimento antes ou junto à certidão'
              : this.registro.certidaoJulgamentoTransferida
                ? 'Certidão transferida em: ' +
                  moment(this.registro.informacoesAdicionais.dataTransferenciaCertidao, 'DD-MM-YYYY-HH-mm-ss').format(
                      'DD/MM/YYYY [às] HH:mm:ss'
                  )
                : 'Enviar certidão para o PJe';
    }

    renderTooltipMovimento() {
        return !this.registro.movimentosCadastrados
            ? 'Movimento não cadastrado'
            : this.registro.movimentosTransferidos
              ? 'Movimento transferido em: ' +
                moment(this.registro.informacoesAdicionais.dataTransferenciaMovimento, 'DD-MM-YYYY-HH-mm-ss').format(
                    'DD/MM/YYYY [às] HH:mm:ss'
                )
              : 'Enviar movimento(s) para o PJe';
    }
}

export default inject('stores')(observer(ModalEnviarProcessoPJe));
